import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "@/components/ui/breadcrumb";
import ProductHeroVisual from "../shared/ProductHeroVisual";
import { useLanguage } from "@/hooks/useLanguage";
import { LucideIcon } from "lucide-react";

interface ProductHeroProps {
  translationPrefix: string;
  heroImage: string;
  imageAlt: string;
  floatingIcons: [
    { icon: LucideIcon; color: "mint" | "orange" | "primary" },
    { icon: LucideIcon; color: "mint" | "orange" | "primary" },
    { icon: LucideIcon; color: "mint" | "orange" | "primary" },
    { icon: LucideIcon; color: "mint" | "orange" | "primary" }
  ];
}

const ProductHeroI18n = ({ translationPrefix, heroImage, imageAlt, floatingIcons }: ProductHeroProps) => {
  const { t, localizePath } = useLanguage();
  const p = (key: string) => t(`${translationPrefix}.hero.${key}`);

  return (
    <section className="relative pt-36 md:pt-48 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/30" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-2">
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={localizePath("/")} className="text-muted-foreground hover:text-primary">{p('breadcrumbProducts')}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-chathero-orange font-medium">{p('breadcrumbCurrent')}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight mb-6 animate-fade-in">{p('title')}</h1>
            <p className="text-xl md:text-2xl text-chathero-orange font-semibold mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>{p('subtitle')}</p>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl animate-fade-in" style={{ animationDelay: "0.2s" }}>{p('description')}</p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <Button size="lg" className="bg-chathero-mint text-accent-foreground hover:bg-chathero-mint/90 text-base px-8" asChild>
                <Link to={localizePath("/kontakt")}>{p('ctaPrimary')}</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-base px-8" asChild>
                <a href="https://app.chathero.ai/register/essential" target="_blank" rel="noopener noreferrer">{p('ctaSecondary')}</a>
              </Button>
            </div>
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 animate-fade-in" style={{ animationDelay: "0.4s" }}>
              {['trustGdpr', 'trustHosting', 'trustReady'].map((key) => (
                <div key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-chathero-mint" />
                  </div>
                  <span>{p(key)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-1 hidden lg:flex justify-center lg:justify-end">
            <ProductHeroVisual imageSrc={heroImage} imageAlt={imageAlt} floatingIcons={floatingIcons} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductHeroI18n;
